import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useScenarioDetail(id) {
  const [scenario, setScenario] = useState(null);

  useEffect(() => {
    let active = true;
    if (!id) {
      setScenario(null);
      return undefined;
    }
    axios
      .get(`${API}/scenarios/${id}`)
      .then(({ data }) => {
        if (active) setScenario(data);
      })
      .catch(() => {
        if (active) setScenario(null);
      });
    return () => {
      active = false;
    };
  }, [id]);

  return scenario;
}

export function useCompareData() {
  const [params, setParams] = useSearchParams();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aId, setAId] = useState(params.get("a") || "");
  const [bId, setBId] = useState(params.get("b") || "");
  const a = useScenarioDetail(aId);
  const b = useScenarioDetail(bId);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/scenarios`);
        if (active) setScenarios(data);
      } catch (e) {
        if (active) toast.error("Could not load scenarios");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const next = new URLSearchParams();
    if (aId) next.set("a", aId);
    if (bId) next.set("b", bId);
    setParams(next, { replace: true });
  }, [aId, bId, setParams]);

  const chartData = useMemo(() => {
    if (!a && !b) return [];
    const len = Math.max(a?.result.yearly.length || 0, b?.result.yearly.length || 0);
    return Array.from({ length: len }, (_, i) => ({
      year: i + 1,
      [`A · ${a?.client_name || ""}`]: a?.result.yearly[i]?.total_wealth_gain ?? null,
      [`B · ${b?.client_name || ""}`]: b?.result.yearly[i]?.total_wealth_gain ?? null,
    }));
  }, [a, b]);

  const opts = useMemo(
    () =>
      scenarios.map((s) => ({
        value: s.id,
        label: `${s.client_name}${s.business_name ? ` · ${s.business_name}` : ""}`,
      })),
    [scenarios]
  );

  return { loading, scenarios, a, b, aId, bId, setAId, setBId, opts, chartData };
}
